import type { ChartCommand } from './chartCommands'
import type { LayoutFailureReason } from './chartLayoutCommands'
import type { ChartCommandApplyEntry, ChartCommandApplyResults } from '../../screener/aiChat/types'

export type { ChartCommandApplyEntry, ChartCommandApplyResults }
export type ChartCommandApplyResult = ChartCommandApplyResults

export function emptyChartCommandApplyResult(): ChartCommandApplyResult {
    return { applied: [], failed: [] }
}

export type ChartCommandFailureReason =
    | 'unsupported_op'
    | 'missing_fields'
    | 'not_found'
    | 'chart_unavailable'
    | 'invalid_value'
    | 'nothing_to_change'

function commandLabel(command: ChartCommand): string {
    const raw =
        command.name
        ?? command.code
        ?? command.symbol
        ?? command.chart_type
        ?? command.type
        ?? command.mode
        ?? command.direction
        ?? command.op
    return String(raw || 'change').trim()
}

function opSpecificNotFound(op: string, label: string): string | null {
    switch (op) {
        case 'add_indicator':
            return `Couldn't add ${label} — that indicator isn't available on this chart.`
        case 'remove_indicator':
            return `Couldn't remove ${label} — that indicator isn't on the chart.`
        case 'update_indicator_inputs':
            return `Couldn't update ${label} — that indicator isn't on the chart.`
        case 'update_indicator_settings':
            return `Couldn't update ${label} — that indicator isn't on the chart.`
        case 'set_chart_type':
            return `Couldn't switch to ${label} — that chart type isn't available.`
        case 'set_symbol':
            return `Couldn't find ${label}. Check the symbol and try again.`
        case 'set_time_bucket':
            return `Couldn't set ${label} — that timeframe isn't available.`
        case 'add_comparison':
        case 'remove_comparison':
            return `Couldn't find ${label} to compare.`
        case 'set_date_range':
            return `Couldn't set ${label} — that date range isn't available.`
        case 'load_saved_layout':
            return "Couldn't find that saved layout."
        case 'add_drawing':
            return `Couldn't add that drawing — check the type and coordinates.`
        case 'remove_drawing':
            return `Couldn't remove that drawing — it wasn't found on the chart.`
        default:
            return null
    }
}

function opSpecificInvalid(op: string, label: string): string | null {
    switch (op) {
        case 'set_scale_mode':
            return `Couldn't set scale to ${label} — that mode isn't supported.`
        case 'set_visible_range':
            return "Couldn't set that visible range — check the from/to dates."
        case 'goto_date':
            return "Couldn't jump to that date — check the format and try again."
        case 'pan':
            return "Couldn't pan the chart — use left or right."
        default:
            return null
    }
}

export function chartCommandFailureMessage(
    command: ChartCommand,
    reason: ChartCommandFailureReason,
): string {
    const op = String(command.op || '')
    const label = commandLabel(command)

    switch (reason) {
        case 'unsupported_op':
            return "That chart change isn't supported yet."
        case 'chart_unavailable':
            return "The chart isn't ready yet. Try again in a moment."
        case 'missing_fields':
            if (op === 'set_symbol' || op === 'add_comparison') {
                return 'That change was missing a symbol.'
            }
            if (op === 'add_indicator') {
                return 'That change was missing an indicator name.'
            }
            if (op === 'add_drawing') {
                return 'That drawing was missing required details.'
            }
            return 'That change was missing required details.'
        case 'not_found':
            return opSpecificNotFound(op, label) ?? `Couldn't apply that change — ${label} wasn't found.`
        case 'invalid_value':
            return opSpecificInvalid(op, label) ?? 'That change used an invalid value.'
        case 'nothing_to_change':
            if (op === 'remove_comparison') {
                return 'No comparison to remove.'
            }
            return 'Nothing to change on the chart.'
    }
}

export function chartCommandSuccessMessage(command: ChartCommand): string {
    const op = String(command.op || '')
    const label = commandLabel(command)

    switch (op) {
        case 'set_symbol':
            return `Switched to ${label}`
        case 'set_time_bucket':
            return `Changed timeframe to ${label}`
        case 'set_chart_type':
            return `Changed chart type to ${label}`
        case 'add_indicator':
            return `Added ${label}`
        case 'remove_indicator':
            return label !== 'change' ? `Removed ${label}` : 'Removed indicator'
        case 'remove_all_indicators':
            return 'Removed all indicators'
        case 'update_indicator_inputs':
            return label !== 'change' ? `Updated ${label}` : 'Updated indicator settings'
        case 'update_indicator_settings':
            return label !== 'change' ? `Updated ${label} settings` : 'Updated indicator settings'
        case 'add_comparison':
            return `Added ${label} for comparison`
        case 'remove_comparison':
            return `Removed ${label} comparison`
        case 'set_scale_mode':
            return `Changed scale to ${label}`
        case 'set_invert_scale':
            return Boolean(command.invert) ? 'Inverted price scale' : 'Restored price scale'
        case 'move_price_scale':
            return 'Moved price scale'
        case 'update_chart_settings':
            return 'Updated chart settings'
        case 'update_series_style':
            return 'Updated candle colors'
        case 'apply_chart_defaults':
            return command.all_panes ? 'Restored default chart styling on all panes' : 'Restored default chart styling'
        case 'set_date_range':
            return `Set date range to ${label}`
        case 'goto_date':
            return 'Jumped to date'
        case 'set_visible_range':
            return 'Adjusted visible range'
        case 'zoom':
            return String(command.direction || 'in').toLowerCase() === 'out' ? 'Zoomed out' : 'Zoomed in'
        case 'pan':
            return `Panned ${String(command.direction || '').toLowerCase()}`
        case 'reset_view':
            return 'Reset chart view'
        case 'goto_latest':
            return 'Jumped to latest candle'
        case 'add_drawing':
            return `Added ${label !== 'change' ? label : 'drawing'}`
        case 'remove_drawing':
            return 'Removed drawing'
        case 'load_saved_layout':
            return label !== 'change' ? `Opened ${label}` : 'Opened saved layout'
        default:
            return 'Updated chart'
    }
}

export function layoutCommandSuccessMessage(command: ChartCommand, detail?: string): string {
    const op = String(command.op || '')
    const suffix = detail ? ` — ${detail}` : ''

    switch (op) {
        case 'apply_layout':
            return `Changed layout to ${detail ?? 'the requested arrangement'}`
        case 'reset_layout':
            return 'Reset to a single chart'
        case 'split_pane':
            return `Split the chart${suffix}`
        case 'close_pane':
            return `Closed ${detail ?? 'the pane'}`
        case 'focus_pane':
            return `Switched to ${detail ?? 'the pane'}`
        case 'equalize_panes':
            return 'Evened out the pane sizes'
        case 'set_layout_sync':
            return `Updated layout sync${suffix}`
        case 'set_pane_widget_type':
            return `Changed the pane to ${detail ?? 'the requested widget'}`
        case 'set_pane_symbol':
            return detail?.includes('pane')
                ? `Loaded ${detail}`
                : `Switched to ${detail ?? 'the symbol'}`
        case 'set_layout_symbols':
            return `Loaded symbols into the layout${suffix}`
        default:
            return 'Updated the layout'
    }
}

/**
 * Failures that mean the chart this tab is bound to is gone (stale tab, chart torn
 * down, configuration never loaded). Retrying in the same tab hits the same wall,
 * so the user is asked to reload instead.
 */
const DISCONNECTED_SURFACE_REASONS = new Set<string>(['chart_unavailable', 'layout_unavailable'])

export function isChartDisconnectedFailure(failed: ChartCommandApplyEntry[]): boolean {
    return (
        failed.length > 0
        && failed.every((entry) => entry.reason != null && DISCONNECTED_SURFACE_REASONS.has(entry.reason))
    )
}

/**
 * Reply text for a turn where the assistant asked for chart changes and none landed.
 * The model writes its reply before the browser applies anything, so its prose claims
 * success — that has to be replaced rather than annotated.
 */
export function chartCommandsAllFailedMessage(failed: ChartCommandApplyEntry[]): string {
    if (isChartDisconnectedFailure(failed)) {
        return "This tab isn't connected to the chart any more, so nothing was changed. Reload the page and try again."
    }
    return failed.map((entry) => entry.message).join(' ')
}

export type ChartCommandTurnResolution = {
    content: string
    showRetry: boolean
    /** Replace streamed assistant text instead of merging with it. */
    replaceContent: boolean
}

/** When every command failed, the agent's success prose must not win over apply results. */
export function resolveChartCommandTurnContent(
    draftContent: string,
    results: ChartCommandApplyResults | null | undefined,
): ChartCommandTurnResolution {
    const appliedCount = results?.applied.length ?? 0
    const failedCount = results?.failed.length ?? 0
    if (results && appliedCount === 0 && failedCount > 0) {
        return {
            content: chartCommandsAllFailedMessage(results.failed),
            showRetry: !isChartDisconnectedFailure(results.failed),
            replaceContent: true,
        }
    }
    return { content: draftContent, showRetry: false, replaceContent: false }
}

export function layoutCommandFailureMessage(
    command: ChartCommand,
    reason: LayoutFailureReason,
): string {
    const op = String(command.op || '')

    switch (reason) {
        case 'layout_unavailable':
            return "The chart layout isn't ready yet. Try again in a moment."
        case 'pane_not_found':
            return "Couldn't find that pane — check which chart you meant."
        case 'unknown_layout':
            return "That layout isn't one of the available arrangements."
        case 'restricted':
            return 'Multi-chart layouts are not available on your current plan.'
        case 'last_pane':
            return "That's the only chart open, so it can't be closed."
        case 'security_not_found':
            return `Couldn't find ${String(command.code || command.symbol || 'that symbol')}. Check the symbol and try again.`
        case 'invalid_value':
            if (op === 'split_pane') {
                return 'A split needs between 2 and 4 panes.'
            }
            if (op === 'set_pane_widget_type') {
                return "That widget type isn't available for a pane."
            }
            return 'That layout change used an invalid value.'
    }
}
